type SchemaNode = Record<string, any>;

interface SeoDates {
  datePublished?: string;
  dateModified?: string;
}

export const toIso8601 = (input: unknown): string | undefined => {
  if (input === null || input === undefined) {
    return undefined;
  }

  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) {
      return undefined;
    }
    return input.toISOString();
  }

  if (typeof input === 'number') {
    const timestamp = input < 1000000000000 ? input * 1000 : input;
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }
    return date.toISOString();
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) {
      return undefined;
    }
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }
    return date.toISOString();
  }

  return undefined;
};

const hasSeoType = (type: unknown): boolean => {
  if (Array.isArray(type)) {
    return type.some(value => hasSeoType(value));
  }

  if (typeof type === 'string') {
    return type === 'WebPage' || type === 'Article' || type.endsWith('Article');
  }

  return false;
};

const removeMisplacedDates = (node: SchemaNode) => {
  if (!node?.hasPart) {
    return;
  }

  if (Array.isArray(node.hasPart)) {
    node.hasPart.forEach(part => {
      if (part && typeof part === 'object') {
        delete part.datePublished;
        delete part.dateModified;
      }
    });
    return;
  }

  if (typeof node.hasPart === 'object') {
    delete node.hasPart.datePublished;
    delete node.hasPart.dateModified;
  }
};

const applyDatesToNode = (node: SchemaNode, dates: SeoDates) => {
  if (!hasSeoType(node?.['@type'])) {
    return;
  }

  const normalizedPublished = dates.datePublished ?? toIso8601(node.datePublished);
  const normalizedModified = dates.dateModified ?? toIso8601(node.dateModified);

  if (normalizedPublished) {
    node.datePublished = normalizedPublished;
  }

  if (normalizedModified) {
    node.dateModified = normalizedModified;
  }

  removeMisplacedDates(node);
};

export const normalizeSeoSchema = (schema: unknown, dates: SeoDates): string | undefined => {
  if (!schema) {
    return undefined;
  }

  let parsedSchema = schema;
  if (typeof schema === 'string') {
    try {
      parsedSchema = JSON.parse(schema);
    } catch (error) {
      return schema;
    }
  }

  if (!parsedSchema || typeof parsedSchema !== 'object') {
    return typeof schema === 'string' ? schema : JSON.stringify(schema);
  }

  const root = parsedSchema as SchemaNode;
  const graph = Array.isArray(root['@graph']) ? root['@graph'] : [root];
  graph.forEach(node => {
    if (node && typeof node === 'object') {
      applyDatesToNode(node, dates);
    }
  });

  return JSON.stringify(root);
};
